import { Alert, Spin, Steps } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Logo } from "~/components/layout/logo.jsx";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { getCurrentClubForNonLoggedInUser } from "~/redux/persist/persist.actions.js";
import StepAanschaf from "./steps/StepAanschaf.jsx";
import StepBevestig from "./steps/StepBevestig.jsx";
import StepDetails from "./steps/StepDetails.jsx";
import StepKado from "./steps/StepKado.jsx";
import StepReservering from "./steps/StepReservering.jsx";

export const VoucherCheckout = () => {
  const { slug, uuid } = useParams();

  // check if giftMode, is /gift/ in url
  const giftMode = window.location.pathname.includes("/gift/");

  // if UUID present, we are in edit-mode, meaning we are editing an existing voucher
  // otherwise we are in create-mode, meaning we are creating a new voucher
  const editMode = Boolean(uuid);

  const [loading, setLoading] = useState(true);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [checkoutItem, setCheckoutItem] = useState({});

  const persist = useSelector((state) => state.persist);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getCurrentClubForNonLoggedInUser());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    async function fetchProduct() {
      setLoading(true);
      setError(null);
      const data = await api.get(
        `financial/prepaid-products/${slug}/details.json`,
      );
      if (isMounted) {
        if (data) {
          // Set checkoutItem with addons, quantity, and total_price for easier updates later
          setProduct(data);
          const addons = data.addons.map((addon) => ({
            ...addon,
            quantity: addon.min_amount || 0,
            price: parseFloat(addon.unit_price || 0) * (addon.min_amount || 0),
          }));

          let currentVoucher = {};
          if (uuid) {
            currentVoucher = await api.get(
              `financial/vouchers/${uuid}/data.json`,
            );
            if (currentVoucher === undefined || !currentVoucher.is_active) {
              setError($t("Deze voucher is niet meer actief of bestaat niet."));
              setLoading(false);
              return;
            }

            // split reserved_timeslot into date and time
            if (currentVoucher.reserved_timeslot) {
              try {
                const [date, time] = currentVoucher.reserved_timeslot
                  .split("+")[0]
                  .split("T");
                currentVoucher.date = date;
                currentVoucher.timeslot = time;
              } catch (e) {
                console.error(
                  "Error parsing reserved_timeslot:",
                  currentVoucher.reserved_timeslot,
                  e,
                );
              }
            }
          }

          setCheckoutItem({
            ...currentVoucher,
            addons,
            total_price:
              parseFloat(data.base_price || 0) +
              addons.reduce((sum, addon) => {
                return sum + addon.price;
              }, 0),
          });
        } else {
          setError(data || "Unknown error");
        }
      }
      setLoading(false);
    }
    fetchProduct();
    return () => {
      isMounted = false;
    };
  }, [slug, uuid]);

  const onPay = async () => {
    setLoading(true);
    setError(null);

    if (editMode) {
      const { data } = await api.put(
        `financial/vouchers/${checkoutItem.id}/details.json`,
        {
          ...checkoutItem,
          product_id: product.id,
        },
      );
      if (data) {
        setSavedSuccess(true);
        setLoading(false);
      }
    } else {
      try {
        const data = await api.post(
          `financial/prepaid-products/create_payment.json`,
          {
            ...checkoutItem,
            product_id: product.id,
          },
        );
        if (data && data.payment_link) {
          // Redirect to the payment link
          window.location.href = data.payment_link;
        } else {
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || "Failed to complete purchase");
        setLoading(false);
      }
    }
  };

  // Steps array must be constructed after this helper
  const steps = [
    !giftMode &&
      !editMode && {
        title: $t("Prijs"),
        content: (
          <StepAanschaf
            product={product}
            checkoutItem={checkoutItem}
            setCheckoutItem={setCheckoutItem}
            onNext={() => setCurrentStep((s) => s + 1)}
          />
        ),
      },
    ...(product?.has_calendar
      ? [
          {
            title: $t("Tijd"),
            content: (
              <StepReservering
                voucherId={checkoutItem.id ?? "0"}
                editMode={editMode}
                product={product}
                checkoutItem={checkoutItem}
                setCheckoutItem={setCheckoutItem}
                onNext={() => setCurrentStep((s) => s + 1)}
                onPrev={() => setCurrentStep((s) => s - 1)}
              />
            ),
          },
        ]
      : []),
    {
      title: $t("Kado"),
      content: (
        <StepKado
          product={product}
          checkoutItem={checkoutItem}
          setCheckoutItem={setCheckoutItem}
          onNext={() => setCurrentStep((s) => s + 1)}
          onPrev={() => setCurrentStep((s) => s - 1)}
          giftMode={giftMode}
        />
      ),
    },
    !giftMode && {
      title: $t("Naam"),
      content: (
        <StepDetails
          product={product}
          checkoutItem={checkoutItem}
          setCheckoutItem={setCheckoutItem}
          onNext={() => setCurrentStep((s) => s + 1)}
          onPrev={() => setCurrentStep((s) => s - 1)}
        />
      ),
    },

    {
      title: $t("Bevestig"),
      content: (
        <StepBevestig
          editMode={editMode}
          product={product}
          checkoutItem={checkoutItem}
          giftMode={giftMode}
          onPrev={() => setCurrentStep((s) => s - 1)}
          onPay={onPay}
        />
      ),
    },
  ].filter((f) => f);

  if (loading)
    return <Spin style={{ display: "block", margin: "40px auto" }} />;

  if (savedSuccess) {
    return (
      <Alert
        type="info"
        message={$t("Je reservering is succesvol aangepast!")}
        style={{ maxWidth: 500, margin: "40px auto" }}
      />
    );
  }
  if (error)
    return (
      <Alert
        type="error"
        message={error}
        style={{ maxWidth: 500, margin: "40px auto" }}
      />
    );
  if (product && product.is_active === false) {
    return (
      <Alert
        type="info"
        message={$t("Product is nu (tijdelijk) niet beschikbaar")}
        style={{ maxWidth: 500, margin: "40px auto" }}
      />
    );
  }
  return (
    <div
      style={{
        maxWidth: 500,
        margin: "40px auto",
        padding: 24,
        background: "#fff",
        borderRadius: 8,
      }}
    >
      <div
        className="row"
        style={{ gap: 6, alignContent: "center", marginBottom: 18 }}
      >
        <Logo url={persist?.club?.logo} width={80} height={30} />
        <h1>{product.name}</h1>
      </div>

      <Steps
        size="small"
        current={currentStep}
        style={{ marginBottom: 24 }}
        items={steps.map((step) => ({ title: step.title }))}
      />

      <div style={{ minHeight: 120, marginBottom: 24 }}>
        {steps[currentStep] ? steps[currentStep].content : null}
      </div>
    </div>
  );
};
